import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { testimonials } from "@/mocks/articles";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9qacl6e3oq4jqumtjb0", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        if (serverMsg && typeof serverMsg === "string" && serverMsg.includes("spam")) {
          setFormError("提交失败，请稍后重试。");
        } else {
          setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后重试。");
        }
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络连接后重试。");
    }
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于打屁股作文",
    description: "打屁股作文是一个专注于惩罚场景作文素材与写作技巧的教育平台，致力于帮助学生提升记叙文写作能力。",
    url: "https://www.dapiguzuowen.com/about",
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <main className="pt-16 bg-background-50">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 max-w-4xl mx-auto py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6">关于我们</h1>

          <div className="prose-custom mb-12">
            <p className="text-base leading-relaxed text-foreground-700 mb-4">
              <strong>打屁股作文</strong>是一个专注于「惩罚场景作文」主题的写作素材与技巧平台。我们致力于为中小学生及写作爱好者提供高质量的惩罚类作文范文、专业的写作技巧指导和丰富的素材积累。
            </p>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">我们的使命</h2>
            <p className="text-base leading-relaxed text-foreground-700 mb-4">
              惩罚场景是记叙文写作中的经典题材，也是最容易引发读者共鸣的主题之一。然而，很多学生在写作中往往流于表面——要么过度渲染暴力细节，要么缺乏情感深度。我们的使命是帮助学生掌握如何在惩罚类作文中平衡细节描写与情感表达，写出真实动人的优秀作品。
            </p>

            <h2 className="text-xl font-bold text-foreground-950 mt-8 mb-4">核心优势</h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mt-6 mb-8">
              <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-article-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">精选范文库</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  精心筛选校园惩罚、家庭惩罚等各类场景的优质范文，每篇附详细赏析点评。
                </p>
              </div>
              <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-lightbulb-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">专业写作指导</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  从神态描写、动作细节到环境烘托，系统讲解惩罚场景的各类写作技法。
                </p>
              </div>
              <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">关注情感教育</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  不仅仅教写作，更注重通过惩罚作文传递成长感悟、亲情教育和人生价值观。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 text-center">用户口碑</h2>
            <p className="text-sm text-foreground-600 mb-10 text-center">听听我们的用户怎么说</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 p-5 md:p-6 rounded-xl border border-background-200/60">
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {t.content}
                  </p>
                  <p className="text-xs font-semibold text-foreground-900">{t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 max-w-2xl mx-auto py-12 md:py-16">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 text-center">联系我们</h2>
          <p className="text-sm text-foreground-600 mb-8 text-center">
            有任何问题或建议，欢迎通过以下表单联系我们
          </p>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60"
          >
            <div className="flex flex-col gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
              </div>

              <div className="absolute opacity-0 pointer-events-none" style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-300 cursor-pointer mt-2"
              >
                提交留言
              </button>
            </div>

            {formStatus === "success" && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                <i className="ri-check-line"></i> 感谢您的留言，我们会尽快回复！
              </div>
            )}

            {formStatus === "error" && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line"></i> {formError || "提交失败，请稍后重试。"}
              </div>
            )}
          </form>
        </section>
      </main>
    </>
  );
}